import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Sensor, SensorGroup, SensorReadingType, TimeZoneDisplay } from '@models';
import { SensorWithGroupList } from '@models/Sensor/sensorWithGroupList.model';
import { SensorReadingSelection, SensorReadingSelectionRequest, SelectionChangeReason } from '@models/Sensor/sensorReadingSelection.model';
import { DateRanges } from '@models/ranges';
import { SensorService } from '@services/sensor.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { CommonShared } from 'app/shared/classes/common';
import { decodeDateRange, presetDateRange, SelectionDateRange } from 'app/shared/classes/dateRangeCodec';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';

// The "Analyze Sensor Readings" left panel as a reusable component: date range,
// displayed time zone, the Sensors-and-Groups tree and the Reading Types list.
//
// It owns the selection UI only. It never loads readings: hosts react to
// (selectionChanged) and fetch whatever they need. A host can restore a saved
// selection (favorite / last used) with applySelection().
@Component({
  standalone: false,
  selector: 'kig-sensor-reading-selector',
  templateUrl: './sensor-reading-selector.component.html',
  styleUrls: ['./sensor-reading-selector.component.scss']
})
export class SensorReadingSelectorComponent implements OnInit {
  // The Original / Calibrated checkboxes only matter to hosts that render a value
  // column (Analyze's tabs); the GeoMap shows a single value, so it hides them.
  @Input() showReadingSourceOptions = true;

  @Output() selectionChanged = new EventEmitter<SensorReadingSelection>();

  // The refresh icon on the Reading Types header (reload the readings).
  @Output() refreshRequested = new EventEmitter<void>();

  @ViewChild('sensorsPanel') sensorsPanel: ElementRef;

  common = new CommonShared();
  ranges = DateRanges;

  // Reference data
  allSensors: Sensor[] = [];
  allSensorGroups: SensorGroup[] = [];
  private sensorsLoaded = false;
  private groupsLoaded = false;
  private pendingRequest: SensorReadingSelectionRequest | null = null;

  get referenceDataLoaded(): boolean {
    return this.sensorsLoaded && this.groupsLoaded;
  }

  // Sensors and Groups tree
  sensorsAndGroupsDisplayed: SensorWithGroupList[] = [];
  searchSensorsText = '';
  selectedSensors: Sensor[] = [];
  selectedSensorGroups: SensorGroup[] = [];

  SensorsAndGroupsCols = [
    { field: 'checked', header: '', width: '10%' },
    { field: 'name', header: 'Sensors and Groups', width: '90%' }
  ];

  // Reading types
  allAvailableReadingTypes: SensorReadingType[] = [];
  readingTypesDisplayed: SensorReadingType[] = [];
  searchReadingTypeText = '';
  selectedReadingTypes: SensorReadingType[] = [];
  private readingTypesRequestId = 0;

  SensorReadingTypesCols = [
    { field: 'checked', header: '', width: '10%' },
    { field: 'name', header: 'Available Sensor Reading Types', width: '90%' }
  ];

  // Date range / time zone / reading source
  selectedDateRange: SelectionDateRange = presetDateRange('Last 3 Days');
  selectedDateRangeName = 'Last 3 Days';
  private rangeJustClicked = false;

  selectedTimeZone: TimeZoneDisplay = TimeZoneDisplay.Local;
  selectedTimeZoneOffset: number = moment().utcOffset();
  showOriginalReadings = true;
  showCalibratedReadings = false;

  // Drag-to-resize of the Sensors and Groups panel.
  sensorsPanelManualHeight = 0;
  private sensorsResizing = false;
  private sensorsResizeStartY = 0;
  private sensorsResizeStartHeight = 0;

  constructor(private sensorService: SensorService,
              private sensorGroupService: SensorGroupService,
              private toastr: ToastrService) {}


  ngOnInit(): void {
    this.loadSavedSettings();
    this.loadSensorsAndGroups();
  }


  // Same localStorage keys as Analyze Sensor Readings, so the choice carries between pages.
  private loadSavedSettings(): void {
    const showOriginalStr = localStorage.getItem('ShowOriginalReadings');
    const showCalibratedStr = localStorage.getItem('ShowCalibratedReadings');

    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(localStorage.getItem('TimeZoneSelected'));
    this.showCalibratedReadings = showCalibratedStr === 'true';
    this.showOriginalReadings = (showOriginalStr == undefined || showOriginalStr === '' || showOriginalStr === 'true');
  }


  private loadSensorsAndGroups(): void {
    this.sensorService.getAllSensorsWithoutTypes().subscribe(
      (results: Sensor[]) => {
        if (results != null) {
          this.allSensors = results;
          this.allSensors.forEach(s => {
            if (s.name == null || s.name === '') {
              s.name = s.uuid;
            }
          });
        } else {
          this.toastr.warning('No sensors found on server');
        }
        this.sensorsLoaded = true;
        this.onReferenceDataLoaded();
      },
      () => {
        this.sensorsLoaded = true;
        this.toastr.error('Error occurred while retrieving sensor list');
        this.onReferenceDataLoaded();
      });

    this.sensorGroupService.getSensorGroupsWithSensors().subscribe(
      (results: SensorGroup[]) => {
        if (results != null) {
          this.allSensorGroups = results;
        } else {
          this.toastr.warning('No Sensor Groups found.');
        }
        this.groupsLoaded = true;
        this.onReferenceDataLoaded();
      },
      () => {
        this.groupsLoaded = true;
        this.toastr.error('Error occurred while retrieving sensor groups');
        this.onReferenceDataLoaded();
      });
  }


  private onReferenceDataLoaded(): void {
    if (!this.referenceDataLoaded) {
      return;
    }

    this.sensorSearchTextChanged();

    if (this.pendingRequest != null) {
      const request = this.pendingRequest;
      this.pendingRequest = null;
      this.applyRequest(request);
    }
  }


  // ---------- public API ----------

  // Restore a saved selection (a favorite or the last used state). Waits for the
  // sensor and group lists if they have not arrived yet. Emits selectionChanged
  // (reason 'restore', byUser false) once the reading types have been checked.
  applySelection(request: SensorReadingSelectionRequest): void {
    if (!this.referenceDataLoaded) {
      this.pendingRequest = request;
      return;
    }
    this.applyRequest(request);
  }


  getSelection(): SensorReadingSelection {
    return this.buildSelection('restore', false);
  }


  // ---------- restoring ----------

  private applyRequest(request: SensorReadingSelectionRequest): void {
    const groupIds = request.sensorGroupIds ?? [];
    const sensorIds = request.sensorIds ?? [];

    // Groups first, then only the individual sensors that are not already covered by
    // one of those groups. A group row has only the favorite's member sensors
    // checked (a favorite may hold a subset); a partial group is auto-expanded so the
    // user sees which members are selected.
    const rows: SensorWithGroupList[] = [];
    const sensorIdsInSelectedGroups = new Set<number>();

    groupIds.forEach(id => {
      const group = this.allSensorGroups.find(g => g.id === id);
      if (group) {
        const groupSensorIds = (group.sensors ?? []).map(s => s.id);
        const checkedIds = groupSensorIds.filter(sid => sensorIds.includes(sid));
        const row = this.makeGroupRow(group, checkedIds);
        row.expanded = row.indeterminate === true;
        rows.push(row);
        groupSensorIds.forEach(sid => sensorIdsInSelectedGroups.add(sid));
      }
    });

    sensorIds.forEach(id => {
      const sensor = this.allSensors.find(s => s.id === id);
      if (sensor && !sensorIdsInSelectedGroups.has(id)) {
        rows.push({ id: sensor.id, name: sensor.name, isSensor: true, isSensorGroup: false, checked: true, count: 1 });
      }
    });

    // Show the restored rows pinned at the top, with the rest of the list below.
    this.sensorsAndGroupsDisplayed = rows;
    this.searchSensorsText = '';
    this.sensorSearchTextChanged();
    this.updateSelectedSensors();

    if (request.dateRange != undefined && request.dateRange !== '') {
      const decoded = decodeDateRange(request.dateRange);
      if (decoded != null) {
        this.selectedDateRange = decoded.range;
        this.selectedDateRangeName = decoded.name;
      }
    }

    if (request.timeZone != undefined && request.timeZone !== '') {
      [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(request.timeZone);
    }
    if (request.showOriginalReadings != undefined) { this.showOriginalReadings = request.showOriginalReadings; }
    if (request.showCalibratedReadings != undefined) { this.showCalibratedReadings = request.showCalibratedReadings; }

    this.loadReadingTypes('restore', false, new Set(request.readingTypeIds ?? []));
  }


  // ---------- Sensors and Groups ----------

  // Rebuild the displayed tree from the search text, keeping every already-checked
  // row (and partially-checked group) pinned.
  sensorSearchTextChanged(): void {
    const isSelected = (row: SensorWithGroupList) =>
      row.checked || (row.isSensorGroup && (row.children ?? []).some(c => c.checked));
    const kept = this.sensorsAndGroupsDisplayed.filter(isSelected);
    const keptSensorIds = new Set(kept.filter(r => r.isSensor).map(r => r.id));
    const keptGroupIds = new Set(kept.filter(r => r.isSensorGroup).map(r => r.id));

    this.searchSensorsText = (this.searchSensorsText ?? '').trim().toLowerCase();
    const search = this.searchSensorsText;

    let sensors = this.allSensors.filter(x => x.name.toLowerCase().includes(search) || (x.uuid ?? '').toLowerCase().includes(search));
    sensors = sensors.slice(0, 500).filter(x => !keptSensorIds.has(x.id));

    let groups = this.allSensorGroups.filter(x => (x.name ?? '').toLowerCase().includes(search));
    groups = groups.slice(0, 150).filter(x => !keptGroupIds.has(x.id));

    this.sensorsAndGroupsDisplayed = kept
      .concat(sensors.map(s => ({ id: s.id, name: s.name, isSensor: true, isSensorGroup: false, checked: false, count: 1 })))
      .concat(groups.map(g => this.makeGroupRow(g, [])));

    this.sensorsAndGroupsDisplayed.sort((a, b) => a.name.localeCompare(b.name));
  }


  // A "Sensors and Groups" row for a group, with a child row per member sensor. A
  // child is checked when its id is in checkedSensorIds.
  private makeGroupRow(group: SensorGroup, checkedSensorIds: number[]): SensorWithGroupList {
    const children: SensorWithGroupList[] = (group.sensors ?? []).map(s => ({
      id: s.id,
      name: s.name,
      isSensor: true,
      isSensorGroup: false,
      checked: checkedSensorIds.includes(s.id),
      count: 1
    }));
    const checkedCount = children.filter(c => c.checked).length;
    return {
      id: group.id,
      name: group.name,
      isSensor: false,
      isSensorGroup: true,
      count: group.sensors != undefined ? group.sensors.length : 0,
      children,
      expanded: false,
      checked: children.length > 0 && checkedCount === children.length,
      indeterminate: checkedCount > 0 && checkedCount < children.length
    };
  }


  onGroupChecked(group: SensorWithGroupList): void {
    (group.children ?? []).forEach(c => c.checked = group.checked);
    group.indeterminate = false;
    this.sensorSelectionChanged();
  }


  onGroupChildChecked(group: SensorWithGroupList): void {
    const children = group.children ?? [];
    const checkedCount = children.filter(c => c.checked).length;
    group.checked = children.length > 0 && checkedCount === children.length;
    group.indeterminate = checkedCount > 0 && checkedCount < children.length;
    this.sensorSelectionChanged();
  }


  toggleGroupExpanded(group: SensorWithGroupList): void {
    group.expanded = !group.expanded;
  }


  groupCheckedCount(group: SensorWithGroupList): number {
    return (group.children ?? []).filter(c => c.checked).length;
  }


  toggleCheckedSensors(): void {
    if (this.sensorsAndGroupsDisplayed.length === 0) {
      return;
    }
    const newState = !this.sensorsAndGroupsDisplayed[0].checked;
    this.sensorsAndGroupsDisplayed.forEach(row => {
      row.checked = newState;
      if (row.isSensorGroup) {
        row.indeterminate = false;
        (row.children ?? []).forEach(c => c.checked = newState);
      }
    });
    this.sensorSelectionChanged();
  }


  // User changed the sensor/group selection. The reading types depend on the
  // sensors, so they are refetched (keeping the ones that are still available
  // checked) and the host is told once they have arrived.
  sensorSelectionChanged(): void {
    this.updateSelectedSensors();
    this.loadReadingTypes('sensors', true, new Set(this.selectedReadingTypes.map(t => t.id)));
  }


  // Rebuild selectedSensors / selectedSensorGroups from the tree, deduped (a sensor
  // may be checked both individually and through a group).
  private updateSelectedSensors(): void {
    const selectedSensors: Sensor[] = [];
    const selectedSensorGroups: SensorGroup[] = [];

    const addSensor = (id: number) => {
      const sensor = this.allSensors.find(s => s.id === id);
      if (sensor && !selectedSensors.some(s => s.id === sensor.id)) {
        sensor.displayedSensorName = this.common.getSensorDisplayName(sensor);
        selectedSensors.push(sensor);
      }
    };

    this.sensorsAndGroupsDisplayed.forEach(row => {
      if (row.isSensor && row.checked) {
        addSensor(row.id);
      }

      if (row.isSensorGroup) {
        // A group with any checked member is a "selected group" (so per-group
        // aggregates keep working); it contributes its checked member sensors.
        const checkedChildren = (row.children ?? []).filter(c => c.checked);
        if (checkedChildren.length > 0) {
          const group = this.allSensorGroups.find(g => g.id === row.id);
          if (group) {
            selectedSensorGroups.push(group);
          }
          checkedChildren.forEach(c => addSensor(c.id));
        }
      }
    });

    this.selectedSensors = selectedSensors;
    this.selectedSensorGroups = selectedSensorGroups;
  }


  // ---------- Reading Types ----------

  // Fetch the reading types of the selected sensors and check the ones in
  // checkedTypeIds, then emit. A newer request supersedes an older one still in flight.
  private loadReadingTypes(reason: SelectionChangeReason, byUser: boolean, checkedTypeIds: Set<number>): void {
    const requestId = ++this.readingTypesRequestId;
    const sensorIds = this.selectedSensors.map(s => s.id);

    if (sensorIds.length === 0) {
      this.setReadingTypes([], checkedTypeIds);
      this.emit(reason, byUser);
      return;
    }

    this.sensorService.getReadingTypesForSensors(sensorIds, '').subscribe(
      (results: SensorReadingType[]) => {
        if (requestId !== this.readingTypesRequestId) {
          return;
        }
        this.setReadingTypes(results ?? [], checkedTypeIds);
        this.emit(reason, byUser);
      },
      () => {
        if (requestId !== this.readingTypesRequestId) {
          return;
        }
        this.toastr.error('Error occurred while retrieving sensor data types');
        this.setReadingTypes([], checkedTypeIds);
        this.emit(reason, byUser);
      });
  }


  private setReadingTypes(types: SensorReadingType[], checkedTypeIds: Set<number>): void {
    this.common.sortArrayByTypeParm(types);
    types.forEach(t => t.checked = checkedTypeIds.has(t.id));

    this.allAvailableReadingTypes = types;
    this.searchReadingTypeText = '';
    this.readingTypesDisplayed = types;
    this.selectedReadingTypes = types.filter(t => t.checked);
  }


  readingTypeSearchTextChanged(): void {
    this.searchReadingTypeText = (this.searchReadingTypeText ?? '').trim().toLowerCase();
    this.readingTypesDisplayed = this.allAvailableReadingTypes.filter(x => x.type.toLowerCase().includes(this.searchReadingTypeText));
  }


  readingTypeSelectionChanged(): void {
    this.selectedReadingTypes = this.allAvailableReadingTypes.filter(x => x.checked);
    this.emit('readingTypes', true);
  }


  toggleCheckedReadings(): void {
    if (this.readingTypesDisplayed.length === 0) {
      return;
    }
    const newState = !this.readingTypesDisplayed[0].checked;
    this.readingTypesDisplayed.forEach(r => r.checked = newState);
    this.readingTypeSelectionChanged();
  }


  // ---------- date range / time zone / reading source ----------

  datesUpdated(quickPick: string): void {
    if (quickPick !== '') {
      const preset = presetDateRange(quickPick);
      if (preset != null) {
        this.selectedDateRange = preset;
      }
      this.selectedDateRangeName = quickPick;
    } else if (this.rangeJustClicked) {
      // Trailing event right after a preset was clicked in the picker: keep the label
      // set by setRangeSelected() so a favorite saves the rolling preset, not fixed dates.
      this.rangeJustClicked = false;
    } else {
      this.selectedDateRangeName = 'Custom Range';
    }

    this.emit('dates', true);
  }


  setRangeSelected(selectedRange: any): void {
    this.selectedDateRangeName = selectedRange.label;
    this.rangeJustClicked = true;

    // rangeClicked emits { label, dates: [start, end] }; keep the { startDate, endDate } shape.
    if (selectedRange.dates != undefined && selectedRange.dates.length === 2) {
      this.selectedDateRange = { startDate: selectedRange.dates[0], endDate: selectedRange.dates[1] };
    }
  }


  selectedTimeZoneChanged(): void {
    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(this.selectedTimeZone);
    localStorage.setItem('TimeZoneSelected', this.selectedTimeZone);
    this.emit('timeZone', true);
  }


  selectedReadingSourceChanged(): void {
    localStorage.setItem('ShowOriginalReadings', this.showOriginalReadings.toString());
    localStorage.setItem('ShowCalibratedReadings', this.showCalibratedReadings.toString());
    this.emit('source', true);
  }


  // ---------- panel sizing ----------

  // Rows visible in the Sensors and Groups list, counting the members of expanded groups.
  get visibleSensorRowCount(): number {
    let count = this.sensorsAndGroupsDisplayed.length;
    this.sensorsAndGroupsDisplayed.forEach(row => {
      if (row.isSensorGroup && row.expanded) {
        count += (row.children ?? []).length;
      }
    });
    return count;
  }


  // Max-height cap of the Sensors and Groups panel: the larger of the content height
  // (grows when a group is expanded) and the height the user dragged it to.
  get sensorsPanelMaxHeightPx(): number {
    const contentHeight = this.visibleSensorRowCount * 43 + 130;
    return Math.max(contentHeight, this.sensorsPanelManualHeight);
  }


  get readingTypesPanelMaxHeightPx(): number {
    return this.readingTypesDisplayed.length * 43 + 130;
  }


  startSensorsResize(event: MouseEvent): void {
    this.sensorsResizing = true;
    this.sensorsResizeStartY = event.clientY;
    this.sensorsResizeStartHeight = (this.sensorsPanel != undefined)
      ? this.sensorsPanel.nativeElement.clientHeight
      : this.sensorsPanelMaxHeightPx;
    event.preventDefault();
  }


  @HostListener('document:mousemove', ['$event'])
  onSensorsResizeMove(event: MouseEvent): void {
    if (!this.sensorsResizing) {
      return;
    }
    const delta = event.clientY - this.sensorsResizeStartY;
    this.sensorsPanelManualHeight = Math.max(160, this.sensorsResizeStartHeight + delta);
  }


  @HostListener('document:mouseup')
  onSensorsResizeEnd(): void {
    this.sensorsResizing = false;
  }


  // ---------- emitting ----------

  private emit(reason: SelectionChangeReason, byUser: boolean): void {
    this.selectionChanged.emit(this.buildSelection(reason, byUser));
  }


  private buildSelection(reason: SelectionChangeReason, byUser: boolean): SensorReadingSelection {
    return {
      reason,
      byUser,
      sensors: this.selectedSensors,
      sensorGroups: this.selectedSensorGroups,
      readingTypes: this.selectedReadingTypes,
      availableReadingTypes: this.allAvailableReadingTypes,
      dateRange: this.selectedDateRange,
      dateRangeName: this.selectedDateRangeName,
      timeZone: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset,
      showOriginalReadings: this.showOriginalReadings,
      showCalibratedReadings: this.showCalibratedReadings
    };
  }
}
